import {
	Dialog,
	DialogPanel,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import {
	Bars3Icon,
	CameraIcon,
	ChatBubbleOvalLeftIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/20/solid';
import {
	DocumentMagnifyingGlassIcon,
	FolderIcon,
	GlobeAltIcon,
	ShoppingCartIcon,
	UserGroupIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import GenerateBlogPost from '../components/GenerateBlogPost';
import Logo from '../components/Global/Logo';
import { useUser } from '../context/UserContext';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

export default function AdminLayout() {
	const { user } = useUser();
	const location = useLocation();
	const navigation = [
		{
			name: 'Home',
			href: '/admin',
			icon: FolderIcon,
			current: location.pathname === '/admin',
		},
		{
			name: 'Emails Collected',
			href: '/admin/customers',
			icon: ShoppingCartIcon,
			current: location.pathname === '/admin/customers',
		},
		{
			name: 'Messages',
			href: '/admin/messages',
			icon: ChatBubbleOvalLeftIcon,
			current: location.pathname === '/admin/messages',
		},
		{
			name: 'Users',
			href: '/admin/users',
			icon: UserGroupIcon,
			current: location.pathname === '/admin/users',
		},
		{
			name: 'Keywords',
			href: '/admin/keywords',
			icon: ChatBubbleOvalLeftIcon,
			current: location.pathname === '/admin/keywords',
		},
		{
			name: 'Blog Posts',
			href: '/admin/blog',
			icon: GlobeAltIcon,
			current: location.pathname === '/admin/blog',
		},
		{
			name: 'AI Images',
			href: '/admin/images',
			icon: CameraIcon,
			current: location.pathname === '/admin/images',
		},
		{
			name: 'Add Testimonials',
			href: '/admin/testimonials',
			icon: DocumentMagnifyingGlassIcon,
			current: location.pathname === '/admin/testimonials',
		},
	];
	const teams = [
		// { id: 3, name: 'Tailwind Labs', href: '#', initial: 'T', current: false },
	];

	const [sidebarOpen, setSidebarOpen] = useState(false);

	return (
		<>
			{/*
        This example requires updating your template:

        ```
        <html class="h-full bg-gray-900">
        <body class="h-full">
        ```
      */}
			<div className='text-black h-full'>
				<Transition.Root
					show={sidebarOpen}
					as={Fragment}
				>
					<Dialog
						as='div'
						className='relative z-50 xl:hidden'
						onClose={setSidebarOpen}
					>
						<TransitionChild
							as={Fragment}
							enter='transition-opacity ease-linear duration-300'
							enterFrom='opacity-0'
							enterTo='opacity-100'
							leave='transition-opacity ease-linear duration-300'
							leaveFrom='opacity-100'
							leaveTo='opacity-0'
						>
							<div className='fixed inset-0' />
						</TransitionChild>

						<div className='fixed inset-0 flex'>
							<TransitionChild
								as={Fragment}
								enter='transition ease-in-out duration-300 transform'
								enterFrom='-translate-x-full'
								enterTo='translate-x-0'
								leave='transition ease-in-out duration-300 transform'
								leaveFrom='translate-x-0'
								leaveTo='-translate-x-full'
							>
								<DialogPanel className='relative mr-16 flex w-full max-w-xs flex-1'>
									<TransitionChild
										as={Fragment}
										enter='ease-in-out duration-300'
										enterFrom='opacity-0'
										enterTo='opacity-100'
										leave='ease-in-out duration-300'
										leaveFrom='opacity-100'
										leaveTo='opacity-0'
									>
										<div className='absolute left-full top-0 flex w-16 justify-center pt-5'>
											<button
												type='button'
												className='-m-2.5 p-2.5'
												onClick={() => setSidebarOpen(false)}
											>
												<span className='sr-only'>Close sidebar</span>
												<XMarkIcon
													className='h-6 w-6 text-black'
													aria-hidden='true'
												/>
											</button>
										</div>
									</TransitionChild>
									{/* Sidebar component, swap this element with another sidebar if you like */}
									<div className='flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 ring-1 ring-white/10'>
										<div className='flex h-16 shrink-0 items-center'>
											<Logo />
										</div>
										<nav className='flex flex-1 flex-col'>
											<ul
												role='list'
												className='flex flex-1 flex-col gap-y-7'
											>
												<li>
													<ul
														role='list'
														className='-mx-2 space-y-1'
													>
														{navigation.map((item) => (
															<li key={item.name}>
																<NavLink
																	to={item.href}
																	className={classNames(
																		item.current
																			? 'bg-gray-800 text-white'
																			: 'text-gray-700 hover:text-white hover:bg-gray-800',
																		'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
																	)}
																>
																	<item.icon
																		className='h-6 w-6 shrink-0'
																		aria-hidden='true'
																	/>
																	{item.name}
																</NavLink>
															</li>
														))}
													</ul>
												</li>
												<li>
													{/* <div className='text-xs font-semibold leading-6 text-gray-400'>
														Your teams
													</div> */}
													<ul
														role='list'
														className='-mx-2 mt-2 space-y-1'
													>
														{teams.map((team) => (
															<li key={team.name}>
																<NavLink
																	to={team.href}
																	className={classNames(
																		team.current
																			? 'bg-gray-800 text-white'
																			: 'text-gray-400 hover:text-white hover:bg-gray-800',
																		'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
																	)}
																>
																	<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white'>
																		{team.initial}
																	</span>
																	<span className='truncate'>{team.name}</span>
																</NavLink>
															</li>
														))}
													</ul>
												</li>
												<li className='-mx-6 mt-auto'>
													<NavLink
														to='/me'
														className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-black hover:text-gray-800'
													>
														<img
															className='h-8 w-8 rounded-full bg-gray-800'
															src={
																user?.profilePicture ||
																'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
															}
															alt=''
														/>
														<span className='sr-only'>Your profile</span>
														<span aria-hidden='true'>{user.name}</span>
													</NavLink>
												</li>
											</ul>
										</nav>
									</div>
								</DialogPanel>
							</TransitionChild>
						</div>
					</Dialog>
				</Transition.Root>

				{/* Static sidebar for desktop */}
				<div className='z-10 hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-72 xl:flex-col bg-secondary/10'>
					{/* Sidebar component, swap this element with another sidebar if you like */}
					<div className='flex grow flex-col gap-y-5 overflow-y-auto bg-secondary/10 px-6 ring-1 ring-white/5'>
						<div className='flex h-16 shrink-0 items-center'>
							<Logo />
						</div>
						<nav className='flex flex-1 flex-col'>
							<ul
								role='list'
								className='flex flex-1 flex-col gap-y-7'
							>
								<li>
									<ul
										role='list'
										className='-mx-2 space-y-1'
									>
										{navigation.map((item) => (
											<li key={item.name}>
												<NavLink
													to={item.href}
													className={classNames(
														item.current
															? 'bg-white text-black'
															: 'text-gray-800 hover:text-black hover:bg-gray-100',
														'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
													)}
												>
													<item.icon
														className='h-6 w-6 shrink-0'
														aria-hidden='true'
													/>
													{item.name}
												</NavLink>
											</li>
										))}
									</ul>
								</li>
								<li>
									<div className='text-xs font-semibold leading-6 text-gray-800'>
										Actions
									</div>
									<ul
										role='list'
										className='-mx-2 mt-2 space-y-1'
									>
										<GenerateBlogPost
											color='white'
											className={
												'text-gray-800 hover:text-black hover:bg-gray-400 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
											}
										/>
										{teams.map((team) => (
											<li key={team.name}>
												<NavLink
													to={team.href}
													className={classNames(
														team.current
															? 'bg-gray-400 text-black'
															: 'text-gray-800 hover:text-black hover:bg-gray-400',
														'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
													)}
												>
													<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white'>
														{team.initial}
													</span>
													<span className='truncate'>{team.name}</span>
												</NavLink>
											</li>
										))}
									</ul>
								</li>
								<li className='-mx-6 mt-auto'>
									<NavLink
										to='/me'
										className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-black hover:bg-gray-800'
									>
										<img
											className='h-8 w-8 rounded-full bg-gray-800'
											src='https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
											alt=''
										/>
										<span className='sr-only'>Your profile</span>
										<span aria-hidden='true'>{user.name}</span>
									</NavLink>
								</li>
							</ul>
						</nav>
					</div>
				</div>

				<div className='xl:pl-72'>
					{/* Sticky search header */}
					<div className='sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-gray-100 text-black px-4 shadow-sm sm:px-6 lg:px-8'>
						<button
							type='button'
							className='-m-2.5 p-2.5 text-black xl:hidden'
							onClick={() => setSidebarOpen(true)}
						>
							<span className='sr-only'>Open sidebar</span>
							<Bars3Icon
								className='h-5 w-5'
								aria-hidden='true'
							/>
						</button>
					</div>

					<main className=''>
						{/* Activity list */}
						<div className='py-10 border-t border-white/10 pt-11 px-8 overflow-scroll'>
							<Outlet />
						</div>
					</main>
				</div>
			</div>
		</>
	);
}
